'use client';

import { AtSign, Copy, RefreshCw, Share2, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo } from 'react';
import { SITE } from '@/app/constants';
import { useCopy } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { useClawFolder } from '../../../_hook/use-claw-folder';
import { CLAW_UPLOAD_ACCEPT, getMediaItemType, normalizePath, reportClawCreationOperation } from '../../../_utils';
import type { ClawFileItem, InsertAssetPayload, MediaViewMode } from '../../../types';
import { CreateFolderDialog } from '../../File/components/CreateFolderDialog';
import { FilePreviewPanel, type FilePreviewPanelHeaderActionContext } from '../../File/components/FilePreviewPanel';
import { useClawFileOperations } from '../../File/hooks/use-claw-file-operations';
import { getItemName, getPreviewType } from '../../File/utils';
import { ActionButton } from './ActionButton';
import { EmptyState } from './EmptyState';
import { useGridColumnCount } from './hooks/use-grid-column-count';
import { useMediaFolderState } from './hooks/use-media-folder-state';
import { useMediaPreview } from './hooks/use-media-preview';
import { useMediaRows } from './hooks/use-media-rows';
import { useShareClawAsset } from './hooks/use-share-claw-asset';
import { MediaBrowserFilters } from './MediaBrowserFilters';
import { MediaBrowserBreadcrumbHeader } from './MediaBrowserHeader';
import { MediaBrowserVirtualList } from './MediaBrowserVirtualList';
import { MediaFileCard } from './MediaFileCard';
import { MediaFolderCard } from './MediaFolderCard';
import { WatermarkDownloadButton } from './WatermarkDownloadButton';

type ClawFolderApi = ReturnType<typeof useClawFolder>;

interface MediaBrowserGridProps {
  activeClawId?: string;
  clawFolderApi: ClawFolderApi;
  viewMode: MediaViewMode;
  onViewModeChange: (mode: MediaViewMode) => void;
  onInsertAsset: (asset: InsertAssetPayload) => void;
  previewTargetFilePath?: string | null;
  onPreviewHandled?: () => void;
  /** 外层正在切 session/恢复目录的窗口期，给面板覆盖一层 loading 避免显示残留 */
  syncing?: boolean;
  /** 外层拖拽 drop 暂存的待上传文件，消费后调用 onPendingDropFilesHandled 清空 */
  pendingDropFiles?: File[] | null;
  onPendingDropFilesHandled?: () => void;
}

export const MediaBrowserGrid = ({
  activeClawId,
  clawFolderApi,
  viewMode,
  onViewModeChange,
  onInsertAsset,
  previewTargetFilePath,
  onPreviewHandled,
  syncing = false,
  pendingDropFiles,
  onPendingDropFilesHandled,
}: MediaBrowserGridProps) => {
  const t = useTranslations();
  const { copy } = useCopy();
  const { bumpClawFileListRefreshKey, clawStatus } = useStore((state) => ({
    bumpClawFileListRefreshKey: state.bumpClawFileListRefreshKey,
    clawStatus: state.clawStatus,
  }));

  const { getCachedPreviewUrl, cachePreviewUrl } = clawFolderApi;

  const folder = useMediaFolderState({ activeClawId, listFolder: clawFolderApi.listFolder });
  const columnCount = useGridColumnCount();
  const mediaList = useMediaRows({
    currentFolderItems: folder.currentFolderItems,
    columnCount,
  });
  const preview = useMediaPreview({
    activeClawId,
    folderPath: folder.folderPath,
    currentFolderItems: folder.currentFolderItems,
    previewTargetFilePath,
    onPreviewHandled,
    onEnterFolder: folder.handleNavigateToPath,
  });

  const refreshStrategy = useMemo(
    () => ({
      afterCreateFolder: () => bumpClawFileListRefreshKey(),
      afterUploadFiles: () => bumpClawFileListRefreshKey(),
      afterDelete: () => bumpClawFileListRefreshKey(),
    }),
    [bumpClawFileListRefreshKey],
  );

  const fileOps = useClawFileOperations({
    activeClawId,
    clawFolderApi,
    refreshStrategy,
    getDefaultTargetFolderPath: () => folder.folderPath,
  });

  const { handleDownloadPath, handleWatermarkDownload, watermarkDownloadingPath, deleteWithConfirm } = fileOps;
  const isChina = SITE === 'china';

  // 外层拖拽 drop 的文件上传到当前所在目录，上传完成后通知外层清空暂存。
  const { handleDropFiles } = fileOps;
  useEffect(() => {
    if (!pendingDropFiles?.length) return;
    void handleDropFiles(pendingDropFiles).finally(() => onPendingDropFilesHandled?.());
  }, [pendingDropFiles, handleDropFiles, onPendingDropFilesHandled]);

  const share = useShareClawAsset({ activeClawId });
  const { handleShareAsset, sharingPath } = share;

  const { handleShare, handleInsert, handleDownload, handleWatermarkDownloadPath, handleDelete } = useMemo(
    () => ({
      handleShare: (item: ClawFileItem, type: 'image' | 'video') => {
        reportClawCreationOperation('share', item.path);
        handleShareAsset(item, type);
      },
      handleInsert: (asset: InsertAssetPayload) => {
        reportClawCreationOperation('addChat', asset.path);
        onInsertAsset(asset);
      },
      handleDownload: (path: string) => {
        reportClawCreationOperation('download', path);
        handleDownloadPath(path);
      },
      handleWatermarkDownloadPath: (path: string) => {
        reportClawCreationOperation('download', path);
        handleWatermarkDownload(path);
      },
      handleDelete: (item: ClawFileItem) => {
        reportClawCreationOperation('delete', item.path);
        deleteWithConfirm(item);
      },
    }),
    [handleShareAsset, onInsertAsset, handleDownloadPath, handleWatermarkDownload, deleteWithConfirm],
  );

  const previewActions = useCallback(
    ({ filePath, isTextPreview, textPreviewContent, refreshTextPreview }: FilePreviewPanelHeaderActionContext) => {
      const previewFile = preview.activePreviewFile;
      const previewPath = filePath || preview.previewFilePath;
      if (!previewPath) return null;

      const previewFileName = previewFile ? getItemName(previewFile) : previewPath.split('/').filter(Boolean).pop();
      const previewFileType = previewFile ? getMediaItemType(previewFile) : null;

      return (
        <>
          {isTextPreview ? (
            <ActionButton
              tooltip={t('claw.file.table.refresh')}
              icon={<RefreshCw className="size-4" />}
              onClick={refreshTextPreview}
            />
          ) : null}
          {textPreviewContent !== undefined ? (
            <ActionButton
              tooltip={t('agentProject.dialogAction.copy')}
              icon={<Copy className="size-4" />}
              onClick={() => copy(textPreviewContent)}
            />
          ) : null}
          {previewFile && (previewFileType === 'image' || previewFileType === 'video') ? (
            <ActionButton
              tooltip={t('claw.assets.actions.share')}
              icon={<Share2 className="size-4" />}
              disabled={sharingPath === previewPath}
              onClick={() => handleShare(previewFile, previewFileType)}
            />
          ) : null}
          {clawStatus === 'CLAW_STATUS_ACTIVE' ? (
            <ActionButton
              tooltip={t('claw.assets.actions.insert')}
              icon={<AtSign className="size-4" />}
              onClick={() =>
                handleInsert({
                  path: previewPath,
                  name: previewFileName || previewPath,
                  type: previewFileType ?? undefined,
                  previewUrl: getCachedPreviewUrl(previewPath),
                })
              }
            />
          ) : null}
          <WatermarkDownloadButton
            showWatermarkOption={isChina && !!previewFileType && previewFileType !== 'file'}
            downloading={watermarkDownloadingPath === previewPath}
            onDownload={() => handleDownload(previewPath)}
            onWatermarkDownload={() => handleWatermarkDownloadPath(previewPath)}
          />
          {previewFile ? (
            <ActionButton
              tooltip={t('claw.assets.actions.delete')}
              icon={<Trash2 className="size-4" />}
              disabled={fileOps.deletingPath === previewPath}
              onClick={() => handleDelete(previewFile)}
            />
          ) : null}
        </>
      );
    },
    [
      clawStatus,
      fileOps.deletingPath,
      getCachedPreviewUrl,
      handleDelete,
      handleDownload,
      handleWatermarkDownloadPath,
      handleInsert,
      handleShare,
      copy,
      preview.activePreviewFile,
      preview.previewFilePath,
      sharingPath,
      isChina,
      watermarkDownloadingPath,
    ],
  );

  const renderFolder = (item: ClawFileItem) => (
    <MediaFolderCard
      item={item}
      deleting={fileOps.deletingPath === item.path}
      onEnter={folder.handleEnterFolder}
      onDelete={handleDelete}
    />
  );

  const renderFile = (item: ClawFileItem) => {
    // 压缩包等无法预览的文件（getPreviewType === 'unknown'）点击封面直接走下载，
    // 避免打开预览面板后只显示「不支持预览」。
    const previewable = getPreviewType(item) !== 'unknown';
    return (
      <MediaFileCard
        item={item}
        clawId={activeClawId}
        clawStatus={clawStatus}
        sharing={sharingPath === item.path}
        deleting={fileOps.deletingPath === item.path}
        watermarkDownloading={watermarkDownloadingPath === normalizePath(item.path)}
        onPreview={previewable ? preview.openPreview : handleDownload}
        onInsert={handleInsert}
        onShare={handleShare}
        onDelete={handleDelete}
        onDownload={handleDownload}
        onWatermarkDownload={isChina ? handleWatermarkDownloadPath : undefined}
        getCachedPreviewUrl={getCachedPreviewUrl}
        onResolvedPreviewUrl={cachePreviewUrl}
      />
    );
  };

  const emptyTitle = mediaList.mediaFilter === 'folder' ? t('claw.assets.emptyFolders') : t('claw.assets.empty');
  const emptyDescription =
    mediaList.mediaFilter === 'folder' ? t('claw.assets.emptyFoldersDescription') : t('claw.assets.emptyDescription');
  const hasNoItems = mediaList.filteredFolderItems.length === 0 && mediaList.filteredFileItems.length === 0;
  const showInitialLoading = syncing || (folder.loading && folder.currentFolderItems.length === 0);

  return (
    <div className="relative flex h-full min-h-0 flex-col border-system-white08 border-l bg-[rgba(255,255,255,0.02)]">
      <div className="border-system-white08 border-b px-4 py-4">
        <MediaBrowserBreadcrumbHeader
          breadcrumbItems={folder.breadcrumbItems}
          uploadingFile={fileOps.uploadingFile}
          loading={folder.loading || syncing}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          onNavigate={folder.handleNavigateToPath}
          onCopyPath={folder.handleCopyCurrentFolderPath}
          onOpenCreateFolder={() => fileOps.createFolderDialog.openFor()}
          onUploadClick={() => fileOps.triggerUpload()}
          onRefresh={folder.handleRefresh}
        />
        <MediaBrowserFilters value={mediaList.mediaFilter} onChange={mediaList.setMediaFilter} />
      </div>

      <input
        ref={fileOps.fileInputRef}
        type="file"
        multiple
        accept={CLAW_UPLOAD_ACCEPT}
        className="hidden"
        onChange={fileOps.handleUploadInputChange}
      />

      <CreateFolderDialog
        open={fileOps.createFolderDialog.open}
        folderName={fileOps.createFolderDialog.folderName}
        creatingFolder={fileOps.creatingFolder}
        onOpenChange={fileOps.createFolderDialog.setOpen}
        onFolderNameChange={fileOps.createFolderDialog.setFolderName}
        onCreate={fileOps.createFolderDialog.handleCreateFolder}
      />

      <div className="relative min-h-0 flex-1">
        {showInitialLoading ? (
          <div className="flex h-full items-center justify-center p-4 text-sm text-system-white40">
            {t('claw.assets.loading')}
          </div>
        ) : mediaList.rows.length > 0 ? (
          <MediaBrowserVirtualList
            rows={mediaList.rows}
            columnCount={columnCount}
            renderFolder={renderFolder}
            renderFile={renderFile}
          />
        ) : !folder.loading && hasNoItems ? (
          <div className="h-full overflow-y-auto p-4">
            <EmptyState title={emptyTitle} description={emptyDescription} />
          </div>
        ) : null}
      </div>

      {preview.previewFilePath ? (
        <div className="absolute inset-0 z-20 bg-[rgba(16,20,26,0.96)] light:bg-[rgba(255,255,255,0.96)]">
          <FilePreviewPanel
            activeFile={preview.activePreviewFile}
            activeFilePath={preview.previewFilePath}
            clawId={activeClawId}
            onClose={preview.closePreview}
            headerActions={previewActions}
            onResolvedPreviewUrl={cachePreviewUrl}
          />
        </div>
      ) : null}
    </div>
  );
};
